const fetchYmmJson = async (fileName, signal) => {
  const cacheBuster = Date.now();
  const response = await fetch(`/cdn/shop/files/${fileName}?v=${cacheBuster}`, { signal });

  if (!response.ok) throw new Error(`YMM: failed to fetch ${fileName}`);

  return response.json();
};

class YmmCollection {
  #makeAbort  = null;
  #modelAbort = null;

  constructor(container) {
    this.container     = container;
    this.collectionUrl = container.dataset.collectionUrl ?? '';
    this.form          = container.querySelector('.js-ymm-collection-form');
    this.formContainer = container.querySelector('.js-ymm-collection-container');
    this.yearSelect    = container.querySelector('.js-ymm-year');
    this.makeSelect    = container.querySelector('.js-ymm-make');
    this.modelSelect   = container.querySelector('.js-ymm-model');
    this.filterBtn     = container.querySelector('.js-ymm-filter');
    this.clearBtn      = container.querySelector('.js-ymm-clear');
    this.vehicleBar    = container.querySelector('.js-ymm-vehicle');
    this.vehicleLabel  = container.querySelector('.js-ymm-vehicle-label');
    this.vehicleRemove = container.querySelector('.js-ymm-vehicle-remove');

    this.#bindEvents();
    this.#populateYears();
    this.#renderVehicle();
  }

  #bindEvents() {
    this.yearSelect.addEventListener('change', () => this.#onYearChange());
    this.makeSelect.addEventListener('change', () => this.#onMakeChange());
    this.modelSelect.addEventListener('change', () => this.#updateFilterBtn());
    this.clearBtn.addEventListener('click', () => this.#onClear());
    this.form.addEventListener('submit', (e) => this.#onSubmit(e));
    this.vehicleRemove.addEventListener('click', () => this.#onVehicleRemove());
  }

  #renderVehicle() {
    const vehicle = localStorage.getItem('vehicle');

    if (vehicle) {
      const handle = localStorage.getItem('vehicle-cat');
      this.vehicleLabel.textContent    = vehicle;
      this.vehicleLabel.href           = handle ? `/collections/${handle}` : '#';
      this.vehicleBar.style.display    = 'block';
      this.formContainer.style.display = 'none';
    } else {
      this.vehicleBar.style.display    = 'none';
      this.formContainer.style.display = 'block';
    }
  }

  #onVehicleRemove() {
    localStorage.removeItem('vehicle');
    localStorage.removeItem('vehicle-cat');
    this.vehicleBar.style.display    = 'none';
    this.formContainer.style.display = 'block';
  }

  async #populateYears() {
    this.yearSelect.disabled = true;

    try {
      const data = await fetchYmmJson('ymm_years.json');
      const placeholder = this.yearSelect.dataset.placeholder ?? '';
      this.yearSelect.innerHTML = `<option value="">${placeholder}</option>`;

      for (const { year } of data.years ?? []) {
        const opt = document.createElement('option');
        opt.value = year;
        opt.textContent = year;
        this.yearSelect.appendChild(opt);
      }
    } catch {
      // silently fail — year select stays empty
    }

    this.yearSelect.disabled = false;
  }

  async #onYearChange() {
    this.#makeAbort?.abort();
    this.#makeAbort = new AbortController();

    this.#reset(this.makeSelect);
    this.#reset(this.modelSelect);
    this.#updateFilterBtn();

    const year = this.yearSelect.value;
    if (!year) return;

    try {
      const data = await fetchYmmJson(`ymm_${year}.json`, this.#makeAbort.signal);
      const makes = data.make ?? [];

      this.#populate(this.makeSelect, makes, 'id', 'title');
      this.makeSelect.disabled = makes.length === 0;
    } catch (e) {
      if (e.name !== 'AbortError') this.#reset(this.makeSelect);
    }

    this.#updateFilterBtn();
  }

  async #onMakeChange() {
    this.#modelAbort?.abort();
    this.#modelAbort = new AbortController();

    this.#reset(this.modelSelect);
    this.#updateFilterBtn();

    const year   = this.yearSelect.value;
    const makeId = this.makeSelect.value;
    if (!year || !makeId) return;

    try {
      const data = await fetchYmmJson(`ymm_${year}_${makeId}.json`, this.#modelAbort.signal);
      const models = data.models ?? [];

      this.#populate(this.modelSelect, models, 'handle', 'title');
      this.modelSelect.disabled = models.length === 0;
    } catch (e) {
      if (e.name !== 'AbortError') this.#reset(this.modelSelect);
    }

    this.#updateFilterBtn();
  }

  #onSubmit(e) {
    e.preventDefault();

    const year       = this.yearSelect.value;
    const makeTitle  = this.makeSelect.selectedOptions[0]?.textContent ?? '';
    const handle     = this.modelSelect.value;
    const modelTitle = this.modelSelect.selectedOptions[0]?.textContent ?? '';

    if (!handle) return;

    const url     = `/collections/${handle}`;
    const vehicle = `${makeTitle} ${modelTitle} ${year}`;

    localStorage.setItem('vehicle', vehicle);
    localStorage.setItem('vehicle-cat', handle);

    window.location.href = url;
  }

  #onClear() {
    if (!this.collectionUrl) return;
    window.location.href = this.collectionUrl;
  }

  #populate(selectEl, items, valueKey, labelKey) {
    const placeholder = selectEl.dataset.placeholder ?? '';
    selectEl.innerHTML = `<option value="">${placeholder}</option>`;

    for (const item of items) {
      const opt = document.createElement('option');
      opt.value = item[valueKey];
      opt.textContent = item[labelKey];
      selectEl.appendChild(opt);
    }
  }

  #reset(selectEl) {
    const placeholder = selectEl.dataset.placeholder ?? '';
    selectEl.innerHTML = `<option value="">${placeholder}</option>`;
    selectEl.value    = '';
    selectEl.disabled = true;
  }

  #updateFilterBtn() {
    const allSelected =
      this.yearSelect.value  !== '' &&
      this.makeSelect.value  !== '' &&
      this.modelSelect.value !== '';

    this.filterBtn.disabled = !allSelected;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  for (const el of document.querySelectorAll('.ymm-collection')) {
    new YmmCollection(el);
  }
});
